import React, { ChangeEvent } from 'react';

import { Paper } from '@mui/material';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import classes from './NgzEntityProfileCard.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { StripeInfoChip } from '../../../payment';
import { PaymentFlowType } from '../../../shared/constants/user-info/paymentFlowType';
import { InbodyTypeKey } from '../../../shared/constants/inbody-info/InbodyType';
import { getHumanizePaymentFlowType } from '../../../shared/utils/userInfoUtils';
import { processInbodyTypes } from './utils/processInbodyTypes';
import { StatusInfo } from './components/StatusInfo/StatusInfo';
import { useEntityStatusInfo } from './hooks/useEntityStatusInfo';
import { getIsNullish } from '../../../shared/utils/jsUtils';
import { UploadAvatarIcon, Point } from '../../../shared/components/ui-components';
import { loggedInUserDataStore } from '../../../shared/stores/loggedInUserDataStore';
import { BillingTypeFlags } from '../../../shared/constants/user-info/billingType';
import { SolutionType } from '../../../shared/constants/user-info/solutionType';

type NgzEntityProfileCardProps = {
  entityId: number;
  gymName?: string;
  avatar?: string | null;
  paymentFlowType?: PaymentFlowType;
  billingTypeFlags: BillingTypeFlags;
  supportedInbodyTypes?: Record<InbodyTypeKey, boolean>;
  allowNonInbodyUsers?: boolean;
  isStripeAccountConnected?: boolean;
  activeSolution: SolutionType | null;
  onAvatarChange: (data: { newAvatar: File }) => void;
};

const NgzEntityProfileCard = observer(
  ({
    entityId,
    gymName,
    avatar,
    paymentFlowType,
    billingTypeFlags,
    supportedInbodyTypes,
    allowNonInbodyUsers,
    isStripeAccountConnected,
    activeSolution,
    onAvatarChange,
  }: NgzEntityProfileCardProps) => {
    const { isEntity } = loggedInUserDataStore;
    const { status } = useEntityStatusInfo(entityId, activeSolution);

    const humanizeInbodyTypes = processInbodyTypes(
      supportedInbodyTypes,
      allowNonInbodyUsers,
    );

    const handleUploadAvatar = async (event: ChangeEvent<HTMLInputElement>) => {
      if (event.target.files) {
        onAvatarChange({ newAvatar: event.target.files[0] });
      }
    };

    return (
      <Paper variant="roundedBox" className={classes.container}>
        <UploadAvatarIcon
          name={gymName}
          avatar={avatar}
          onAvatarChange={handleUploadAvatar}
          disabled={!isEntity}
        />
        <div className={classes.infoBox}>
          <h3 className={classNames(classes.name, 'private-info')}>{gymName}</h3>
          <div className={classes.descriptionBox}>
            {paymentFlowType && (
              <span className="private-info">
                {getHumanizePaymentFlowType(paymentFlowType)}
              </span>
            )}

            {!!humanizeInbodyTypes.length && (
              <>
                <Point />
                <div className={classNames(classes.inbodyInfo, 'private-info')}>
                  {humanizeInbodyTypes.map((inbodyType) => (
                    <span key={inbodyType}>{inbodyType}</span>
                  ))}
                </div>
              </>
            )}
            <Point />
            {status && <StatusInfo status={status} />}
          </div>
        </div>
        <div className={classes.extraOptionsBox}>
          {billingTypeFlags.AUTOMATED_BILLING &&
            !getIsNullish(isStripeAccountConnected) && (
              <StripeInfoChip isStripeConnected={isStripeAccountConnected} />
            )}
        </div>
      </Paper>
    );
  },
);

angularizeWithProviders(NgzEntityProfileCard, 'ngzEntityProfileCard', {
  entityId: '<',
  gymName: '<',
  avatar: '<',
  paymentFlowType: '<',
  billingTypeFlags: '<',
  supportedInbodyTypes: '<',
  allowNonInbodyUsers: '<',
  isStripeAccountConnected: '<',
  activeSolution: '<',
  onAvatarChange: '&',
});

export { NgzEntityProfileCard };
